import { CheckIcon, CloseIcon } from "@chakra-ui/icons";
import { Box, Button as ChakraButton, Flex, Show, Text } from "@chakra-ui/react";
import dayjs from "dayjs";
import "dayjs/locale/sv";
import { Link } from "react-router-dom";
import { useState } from "react";
import styled from "styled-components";
import { appTheme } from "@styles/Theme";
import {
	capitalize,
	convertHoursToMinutes,
	convertMinutesToHours,
	formatDateTime,
	formatShiftHoursRange,
	isTimeReportAttestet,
	isTimeReportDeclined,
	isTimeReportManaged,
	timeReportStatus,
} from "@utils/utils";
import Modal from "./Modal";
import { Tag } from "./Tag";
import { Button } from "./button/Button";
import { EditableText } from "./form/EditableText";
import { TextArea } from "./form/TextArea";
import BigDown from "./icons/BigDown";
import BigUp from "./icons/BigUp";
import Lock from "./icons/Lock";

// TODO: Phase 4 - Import generated API client types
// import type { OoJobService } from "@api/generated/...";

dayjs.locale("sv");

interface Props {
	timeReport: any; // TODO: Replace with OoJobService.GetTimereportEntryIdResponse
	onRowClick: () => any;
	onUpdateTimereport: (status: any, declineComment?: string) => any; // TODO: Replace with proper type
	isExpanded?: boolean;
}

export const TimeReportRow: React.FC<Props> = ({ timeReport, onRowClick, onUpdateTimereport, isExpanded }) => {
	const timeReportAttested: boolean = isTimeReportAttestet(timeReport.status);
	const timeReportDeclined: boolean = isTimeReportDeclined(timeReport.status);

	const updatedDate = timeReport?.metadata?.customerAttestedAt || timeReport.metadata?.customerDeclinedAt;
	const [showDeclineModal, setShowDeclineModal] = useState(false);
	const [showAttestModal, setShowAttestModal] = useState(false);

	const [declineComment, setDeclineComment] = useState("");

	const onDeclinedClick = () => {
		if (timeReportDeclined) {
			setShowAttestModal(true);
		}
	};

	const renderDeclineModal = () => {
		return (
			<Modal
				onClose={() => setShowDeclineModal(false)}
				show={showDeclineModal}
				title="Beskriv varför tidrapporten ej godkänns / reklameras"
			>
				<TextArea value={declineComment || " "} onChangeText={setDeclineComment} />

				<div style={{ marginTop: "20px" }}>
					<Button
						title="Bekräfta"
						disabled={declineComment.length === 0}
						onClick={() => {
							onUpdateTimereport("DECLINED_BY_CUSTOMER", declineComment);
							setDeclineComment("");
							setShowDeclineModal(false);
						}}
						type="success"
					/>
				</div>
			</Modal>
		);
	};

	const renderAttestModal = () => {
		return (
			<Modal onClose={() => setShowAttestModal(false)} show={showAttestModal} title="Attestera">
				<div style={{ marginTop: "20px" }}>
					<Button
						title="Bekräfta"
						onClick={() => {
							onUpdateTimereport("ATTESTED_BY_CUSTOMER");
							setDeclineComment("");
							setShowAttestModal(false);
						}}
						type="success"
					/>
				</div>
			</Modal>
		);
	};

	return (
		<Flex
			direction={"column"}
			justifyContent={"center"}
			cursor={"pointer"}
			padding="13px 5px 0 5px"
			height={{
				0: isExpanded ? (updatedDate ? "380px" : "286px") : "100%",
				md: isExpanded ? (updatedDate ? "306px" : "286px") : "100%",
			}}
			className={isExpanded ? "expanded" : ""}
			onClick={onRowClick}
			borderTop="1px"
			borderTopColor={"divider"}
			_last={{
				borderBottom: "1px",
				borderBottomColor: "divider",
			}}
			_hover={{
				bg: "gray.50",
			}}
		>
			<Styles>
				<Flex align="center" direction={"row"} justifyContent={"center"} gap={"0.5rem"}>
					<Flex flex="1" fontSize={"0.9rem"} width={"285px"} direction={"column"}>
						<div className="row-1">{capitalize(dayjs(timeReport.reported.start).format("ddd D/M"))}</div>

						<div className="row-2">
							{dayjs(timeReport.reported.start).format("HH:mm")}
							{" - "}
							{dayjs(timeReport.reported.end).format("HH:mm")}
						</div>
						<div className="row-3">
							{timeReport.job.customer?.name || ""}, {timeReport.consultant.firstName} {timeReport.consultant.lastName}
						</div>
					</Flex>
					<Show above="md">
						<div className="col-time">
							{timeReport.invoicedTime ? convertMinutesToHours(timeReport.invoicedTime) + " h" : ""}
						</div>
					</Show>

					<Flex textAlign="right" align={"center"}>
						{timeReport.invoiced ? (
							<Lock />
						) : isTimeReportManaged(timeReport.status) ? (
							<Tag
								text={timeReportStatus(timeReport?.status)}
								size="s"
								background={timeReportAttested || timeReportDeclined ? "white" : appTheme.colors.textDimmed}
								textColor={
									timeReportAttested ? appTheme.colors.success : timeReportDeclined ? appTheme.colors.darkRed : "white"
								}
								width="100px"
								height="27px"
								icon={timeReportAttested ? "green-check" : timeReportDeclined ? "red-close" : undefined}
							/>
						) : (
							<Flex direction={{ base: "column", md: "row" }} gap="2">
								<ChakraButton
									bg="origoGreen.50"
									borderColor={"origoGreen.500"}
									borderWidth="1px"
									textColor={"origoGreen.600"}
									size={"xs"}
									leftIcon={<CheckIcon color="origoGreen.600" />}
									w="90px"
									onClick={(e) => {
										e.stopPropagation();
										onUpdateTimereport("ATTESTED_BY_CUSTOMER", "");
									}}
									rounded={2}
								>
									Attestera
								</ChakraButton>

								<ChakraButton
									bg="red.50"
									borderColor={"red.500"}
									borderWidth="1px"
									textColor={"red.600"}
									size={"xs"}
									leftIcon={<CloseIcon boxSize={"11px"} color="red.400" />}
									w="90px"
									rounded={2}
									onClick={(e) => {
										e.stopPropagation();
										setShowDeclineModal(true);
									}}
								>
									Neka
								</ChakraButton>
							</Flex>
						)}
					</Flex>

					{renderDeclineModal()}
					{renderAttestModal()}
				</Flex>

				{isExpanded && (
					<>
						<div className="expanded-row">
							<div className="col-left">
								<p onClick={(e) => e.stopPropagation() /* to avoid collaps when clicking the link */}>
									Uppdrag{" "}
									<Link to={`/uppdrag/${timeReport.job.id}`}>
										<a style={{ textDecoration: "underline " }}>#{timeReport.job.orderNumber}</a>
									</Link>
								</p>

								<p>
									Planerad tid: {formatShiftHoursRange(timeReport?.shift?.start || "", timeReport?.shift?.end || "")}
								</p>
								<p>
									Rapporterad tid:{" "}
									{formatShiftHoursRange(timeReport?.reported?.start || "", timeReport?.reported?.end || "")}
								</p>
								<p>
									Debiterad tid:{" "}
									{timeReport?.invoiceOverride?.start && timeReport?.invoiceOverride?.end
										? formatShiftHoursRange(
												timeReport?.invoiceOverride?.start || "",
												timeReport?.invoiceOverride?.end || ""
										  )
										: "-"}
								</p>
								{timeReport?.extraTimeOrderedBy && <p>Mertid beställd av: {timeReport?.extraTimeOrderedBy}</p>}
								<p>Rast: {convertHoursToMinutes(timeReport.reported.break || 0) || "0"} min</p>
								<p>Avdrag: {timeReport.reported.deduction || 0} SEK</p>
							</div>

							<div className="col-right">
								{isTimeReportDeclined(timeReport.status) && timeReport.declineComment && (
									<EditableText
										hint="(tryck på texten för att redigera)"
										value={timeReport.declineComment || ""}
										onSave={(updatedComment: string) => {
											if (updatedComment !== "") {
												setDeclineComment(updatedComment);
												onUpdateTimereport("DECLINED_BY_CUSTOMER", updatedComment);
											}
										}}
										className="denied"
										disableIfEmpty={true}
									/>
								)}
							</div>
						</div>

						{updatedDate && (
							<Text fontSize="xs" color="muted.600">
								Senast uppdaterad {formatDateTime(updatedDate)}
							</Text>
						)}
					</>
				)}

				<Flex align="center" direction={"row"} justifyContent={"center"} width={"100%"}>
					<Box className="arrow" display={{ 0: "block", md: "none" }} padding={"0.5rem 1rem"}>
						{isExpanded ? (
							<BigUp height={20} width={20} color="#bbb" />
						) : (
							<BigDown height={20} width={20} color="#bbb" />
						)}
					</Box>
				</Flex>
			</Styles>
		</Flex>
	);
};

const Styles = styled.div`
	&.expanded {
		border-color: ${(props) => props.theme.colors.darkGray};
	}

	.arrow-container {
		height: 18px;
	}

	&:hover {
		.arrow {
			display: block;
			cursor: pointer;
		}
	}

	.expanded-row {
		height: 100%;
		width: 100%;
		font-size: 15px;
		font-weight: 400;
		line-height: 19px;
		padding-top: 20px;
		display: flex;
		flex-direction: row;

		.col-left,
		.col-right {
			width: 50%;
			height: 100%;
			flex-direction: column;
		}

		p {
			margin: 7px 0 0 0;
			line-height: 19px;
		}

		.denied {
			color: ${(props) => props.theme.colors.darkRed};
			font-style: italic;
			font-weight: 400;
			text-align: right;
			textarea {
				min-height: 100px;
				height: 100px;
			}
		}
	}

	.updated {
		color: #979797;
		font-size: 14px;
		font-weight: 400;
		line-height: 17px;
		width: 100%;
		text-align: right;
		margin: 7px 0;
	}

	.col-time {
		width: 170px;
		font-size: 15px;
		font-weight: 600;
		line-height: 19px;
	}

	.row-1 {
		color: ${(props) => props.theme.colors.textDimmed};
		font-size: 14px;
		font-weight: 400;
		line-height: 17px;
	}

	.row-2 {
		font-weight: 600;
		margin: 0.25rem 0;
		color: ${(props) => props.theme.colors.dark};
		font-size: 15px;
		line-height: 17px;
	}

	.row-3 {
		margin: 0.25rem 0;
		color: ${(props) => props.theme.colors.dark};
		font-size: 16px;
		font-weight: 400;
		line-height: 19px;
	}

	.clickable {
		margin-left: 15px;
		cursor: pointer;

		&:hover {
			opacity: 0.5;
		}
	}

	.input-field {
		margin: 20px 0;
	}
`;
